/**
 * MapLibre + OpenFreeMap provider — modern vector tiles, no API key needed.
 * Also handles the optional 'satellite' style toggle via provider prop.
 *
 * Adds two provider IDs:
 *   - "maplibre"           street style (Positron by OpenFreeMap)
 *   - "maplibre-satellite" raster satellite (Esri World Imagery — free tier)
 *
 * Marker clustering is done client-side with supercluster so pins stay snappy
 * even with hundreds of POIs.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import Supercluster from "supercluster";
import { registerMapProvider } from "@/components/map/MapProvider";
import { markerElement, clusterElement, markerPxForZoom } from "@/lib/mapSprites";

const STYLE_STREET   = "https://tiles.openfreemap.org/styles/positron";
const STYLE_LIQUID   = "https://tiles.openfreemap.org/styles/liberty";

// Clustering config — kept in one place so future per-category overrides
// slot in without a map rewrite (see /app/frontend/src/lib/poiCategories.js).
const CLUSTER_RADIUS   = 50;   // px
const CLUSTER_MAX_ZOOM = 14;
// Esri World Imagery — public / no key, keep attribution
const SATELLITE_STYLE = {
  version: 8,
  sources: {
    "esri-imagery": {
      type: "raster",
      tiles: [
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      ],
      tileSize: 256,
      attribution: "Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community",
    },
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [
    { id: "esri-imagery",  type: "raster", source: "esri-imagery" },
  ],
};

function MapLibreView(props) {
  const isSatellite = props["data-provider"] === "maplibre-satellite";
  const container = useRef(null);
  const map = useRef(null);
  const markerRefs = useRef([]);
  const [ready, setReady] = useState(false);

  // Bootstrap the map once.
  useEffect(() => {
    if (map.current || !container.current) return;
    map.current = new maplibregl.Map({
      container: container.current,
      style:     isSatellite ? SATELLITE_STYLE : STYLE_STREET,
      center:    props.center ? [props.center[1], props.center[0]] : [72.8777, 19.0760],
      zoom:      props.zoom || 12,
      attributionControl: true,
      cooperativeGestures: false,
    });
    map.current.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.current.on("load", () => setReady(true));
    map.current.on("error", (e) => { /* silent — tiles fall back */ void e; });
    return () => {
      try { map.current?.remove(); } catch { /* ignore */ }
      map.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recenter when props.center changes.
  useEffect(() => {
    if (!ready || !map.current || !props.center) return;
    map.current.easeTo({ center: [props.center[1], props.center[0]], zoom: props.zoom || map.current.getZoom(), duration: 800 });
  }, [ready, props.center?.[0], props.center?.[1], props.zoom]);   // eslint-disable-line

  // Bubble map clicks up to consumers as {lat, lng} so features like the
  // Waypoint Playground can drop pins on tap. Kept behind a prop guard so
  // consumers who don't need clicks aren't affected.
  useEffect(() => {
    if (!ready || !map.current || !props.onMapClick) return;
    const handler = (ev) => {
      try { props.onMapClick({ lat: ev.lngLat.lat, lng: ev.lngLat.lng }); }
      catch { /* silent */ }
    };
    map.current.on("click", handler);
    return () => { map.current?.off("click", handler); };
  }, [ready, props.onMapClick]);

  // Bubble viewport bounds to consumers on moveend (debounced by the browser
  // event loop already — MapLibre coalesces continuous pan/zoom into a
  // single moveend). Enables bounds-aware POI fetching without the map
  // consumer needing a ref to the underlying MapLibre instance.
  useEffect(() => {
    if (!ready || !map.current || !props.onBoundsChange) return;
    const m = map.current;
    const emit = () => {
      try {
        const b = m.getBounds();
        props.onBoundsChange({
          south: b.getSouth(), west: b.getWest(),
          north: b.getNorth(), east: b.getEast(),
          zoom:  m.getZoom(),
        });
      } catch { /* silent */ }
    };
    m.on("moveend", emit);
    // Fire once on ready so consumers get an initial bbox without waiting
    // for the user to pan.
    emit();
    return () => { m.off("moveend", emit); };
  }, [ready, props.onBoundsChange]);

  // Long-press → drop-a-pin (Phase 3 · GPS-safe user POI tagging).
  // Fires the `onLongPress({lat,lng})` callback for BOTH desktop
  // right-click (`contextmenu`) AND a hold-left-click / hold-touch of
  // ≥500 ms. Two independent input paths so the same tag flow works
  // on every device without a per-page hack.
  useEffect(() => {
    if (!ready || !map.current || !props.onLongPress) return;
    const m = map.current;
    const HOLD_MS = 500;
    const CANCEL_PX = 8;
    let timer = null, startPx = null, startLngLat = null;

    // Path 1: desktop right-click. Instant, always available.
    const ctx = (ev) => {
      ev.preventDefault?.();
      try { props.onLongPress({ lat: ev.lngLat.lat, lng: ev.lngLat.lng }); }
      catch { /* silent */ }
    };
    m.on("contextmenu", ctx);

    // Path 2: press-and-hold for touch AND mouse. Cancelled by movement
    // beyond CANCEL_PX so an accidental drag never fires a tag.
    const clear = () => { if (timer) { clearTimeout(timer); timer = null; } startPx = startLngLat = null; };
    const down = (ev) => {
      startPx     = ev.point;
      startLngLat = ev.lngLat;
      timer = setTimeout(() => {
        if (!startLngLat) return;
        try { props.onLongPress({ lat: startLngLat.lat, lng: startLngLat.lng }); }
        catch { /* silent */ }
        clear();
      }, HOLD_MS);
    };
    const move = (ev) => {
      if (!timer || !startPx) return;
      const dx = ev.point.x - startPx.x, dy = ev.point.y - startPx.y;
      if (Math.sqrt(dx * dx + dy * dy) > CANCEL_PX) clear();
    };
    const up = () => clear();

    m.on("mousedown",  down);
    m.on("mousemove",  move);
    m.on("mouseup",    up);
    m.on("touchstart", down);
    m.on("touchmove",  move);
    m.on("touchend",   up);
    m.on("dragstart",  clear);
    m.on("zoomstart",  clear);

    return () => {
      m.off("contextmenu", ctx);
      m.off("mousedown",  down); m.off("mousemove", move); m.off("mouseup",   up);
      m.off("touchstart", down); m.off("touchmove", move); m.off("touchend",  up);
      m.off("dragstart",  clear); m.off("zoomstart", clear);
      clear();
    };
  }, [ready, props.onLongPress]);

  // Optional dashed polylines (used by the Waypoint Playground). Each entry
  // is { id, path: [[lat,lng]...], color, dashed }. Rebuilt on every change.
  useEffect(() => {
    if (!ready || !map.current) return;
    const m = map.current;
    // Tear down any previous playground layers/sources before rebuilding.
    const prevIds = m.__fnLineIds || [];
    for (const id of prevIds) {
      try { if (m.getLayer(id))  m.removeLayer(id); } catch { /* ignore */ }
      try { if (m.getSource(id)) m.removeSource(id); } catch { /* ignore */ }
    }
    const newIds = [];
    (props.polylines || []).forEach((pl, i) => {
      const id = `fn-line-${pl.id || i}`;
      const coords = (pl.path || []).map(([lat, lng]) => [lng, lat]);
      if (coords.length < 2) return;
      try {
        m.addSource(id, {
          type: "geojson",
          data: { type: "Feature", geometry: { type: "LineString", coordinates: coords }, properties: {} },
        });
        m.addLayer({
          id, source: id, type: "line",
          paint: {
            "line-color":     pl.color || "#f59e0b",
            "line-width":     pl.width || 4,
            "line-opacity":   pl.opacity ?? 0.9,
            ...(pl.dashed ? { "line-dasharray": [2, 2] } : {}),
          },
          layout: { "line-cap": "round", "line-join": "round" },
        });
        newIds.push(id);
      } catch { /* silent — probably layer already exists mid-hot-reload */ }
    });
    m.__fnLineIds = newIds;
  }, [ready, props.polylines]);

  // Cluster + render markers whenever inputs change.
  const points = useMemo(() => {
    return (props.markers || []).map((m) => ({
      type: "Feature",
      properties: { ...m, cluster: false },
      geometry: { type: "Point", coordinates: [m.lng, m.lat] },
    }));
  }, [props.markers]);

  useEffect(() => {
    if (!ready || !map.current) return;
    // Wipe old markers cheaply.
    markerRefs.current.forEach((m) => m.remove());
    markerRefs.current = [];
    if (!points.length) return;
    const sc = new Supercluster({ radius: CLUSTER_RADIUS, maxZoom: CLUSTER_MAX_ZOOM });
    sc.load(points);
    const m = map.current;

    // Single reusable renderer — keeps sprite/cluster logic in ONE place
    // so future category additions never require touching this file.
    const paint = () => {
      markerRefs.current.forEach((mk) => mk.remove());
      markerRefs.current = [];
      const nb = m.getBounds();
      const bbox = [nb.getWest(), nb.getSouth(), nb.getEast(), nb.getNorth()];
      const z    = Math.round(m.getZoom());
      const sizePx = markerPxForZoom(z);
      sc.getClusters(bbox, z).forEach((c) => {
        const [lng, lat] = c.geometry.coordinates;
        let el;
        if (c.properties.cluster) {
          el = clusterElement({
            count: c.properties.point_count_abbreviated,
            onClick: () => {
              const zEx = sc.getClusterExpansionZoom(c.properties.cluster_id);
              m.easeTo({ center: [lng, lat], zoom: zEx });
            },
          });
        } else {
          const p = c.properties;
          el = markerElement({
            p, sizePx,
            onClick: (marker) => props.onMarkerClick?.(marker),
          });
        }
        const marker = new maplibregl.Marker({ element: el, anchor: "bottom" })
          .setLngLat([lng, lat])
          .addTo(m);
        if (!c.properties.cluster && c.properties.popup) {
          const popupContent = typeof c.properties.popup === "string"
            ? c.properties.popup
            : (c.properties.title || c.properties.name || c.properties.category || "Place");
          marker.setPopup(
            new maplibregl.Popup({ offset: 20, closeButton: false })
              .setHTML(`<div style='font-weight:700;font-size:13px'>${popupContent}</div>`),
          );
        }
        markerRefs.current.push(marker);
      });
    };

    paint();
    m.on("moveend", paint);
    return () => { m.off("moveend", paint); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, points]);

  return (
    <div className="w-full h-full relative" style={{ minHeight: props.height || 300 }} data-testid="maplibre-container">
      <style>{`@keyframes fnl-pulse{0%{box-shadow:0 0 0 0 rgba(15,118,110,0.65)}70%{box-shadow:0 0 0 12px rgba(15,118,110,0)}100%{box-shadow:0 0 0 0 rgba(15,118,110,0)}}`}</style>
      <div ref={container} className="w-full h-full absolute inset-0"/>
    </div>
  );
}

registerMapProvider({
  id:        "maplibre",
  name:      "MapLibre · OpenFreeMap",
  available: () => true,
  Component: MapLibreView,
});

registerMapProvider({
  id:        "maplibre-satellite",
  name:      "Satellite · Esri",
  available: () => true,
  Component: MapLibreView,
});

export default MapLibreView;
export { STYLE_STREET, STYLE_LIQUID, SATELLITE_STYLE };
